<template>
  <GfrLayoutAside class="app-docs-aside">
    <div class="app-docs-aside-inner">
      <div v-if="hasFilter" class="mb-5 flex flex-shrink-0 max-lg:mb-8">
        <GfrNewButton
          class="app-toggle-btn inline-flex h-18 w-18 text-primary lg:!hidden ltr:mr-5 rtl:ml-5"
          @click="closeMenu"
        >
          <IconsGreatArrow class="h-10 w-6.5 rtl:rotate-180" />
        </GfrNewButton>
        <DocumentInputFilter
          v-model="filterValue"
          v-model:trigger="filterTrigger"
          class="flex-1"
          :placeholder="formatTransify('UGC_OFFICIAL_WEBSITE_FILTER_TEXT')"
        />
        <GfrTooltip
          v-if="openObsoletedBtn"
          :message="
            showObsoleted
              ? formatTransify('UGC_OFFICIAL_WEBSITE_OBSOLETE_TIPS1')
              : formatTransify('UGC_OFFICIAL_WEBSITE_OBSOLETE_TIPS2')
          "
          class="ltr:ml-4 rtl:mr-4"
          content-class="max-lg:hidden"
        >
          <DocumentObsolete v-model="showObsoleted" />
        </GfrTooltip>
      </div>
      <div v-if="tabList.length > 1" class="app-docs-tabs mb-5 flex-shrink-0 max-lg:mb-8">
        <div
          v-for="item in tabList"
          :key="item.value"
          :class="['app-docs-tab']"
          :data-state="item.value === activeTab ? 'active' : 'inactive'"
          @click="toggleTab(item.value)"
        >
          <span class="-mt-0.5 max-lg:-mt-1">{{ item.label }}</span>
        </div>
      </div>
      <div v-show="filterTrigger" class="mb-2.5 text-lg max-lg:mb-5" v-html="formatFilterTip" />
      <GfrScrollArea
        v-model:reset="resetScroll"
        class="app-docs-catalog-wrapper"
        :initial-y="scrollAreaY"
        :dir="$route.params.lang === 'ar' ? 'rtl' : 'ltr'"
      >
        <GfrCatalog
          :data="currentCatalog"
          :active-url="String($route.params.id)"
          :prefix-path="prefixPath"
          :show-obsoleted="hasObsoleted"
        />
      </GfrScrollArea>
      <!--      </template>-->
    </div>
  </GfrLayoutAside>
</template>
<script lang="ts" setup>
import { PropType, ref } from 'vue'
import DocumentObsolete from '~/components/common/Document/Obsolete.vue'
import DocumentInputFilter from '~/components/common/Document/InputFilter.vue'
import type { CatalogsConfig, TabConfig } from '~/components/gfr/types/components'
const props = defineProps({
  catalog: {
    type: Array as PropType<CatalogsConfig[]>,
    required: true
  },
  prefixPath: {
    type: String,
    required: true
  },
  openObsoletedBtn: {
    type: Boolean,
    default: false
  },
  hasFilter: {
    type: Boolean,
    default: true
  },
  tabList: {
    type: Array as PropType<TabConfig[]>,
    default: () => [
      {
        value: 'all',
        label: 'all'
      }
    ]
  },
  activeTab: {
    type: String,
    default: 'all'
  }
})
const emit = defineEmits(['update:active-tab', 'close-menu'])
const route = useRoute()
const store = useStore()
const { formatTransify } = store
const documentStore = useDocumentStore()
const { setDocuments } = documentStore
const resetScroll = ref(false)
const filterValue = ref('')
const filterTrigger = ref(false)
const showObsoleted = ref(false)
const showMenu = ref(false)
const scrollAreaY = ref(0)
const currentCatalog = computed(() => {
  let data = props.catalog
  if (filterTrigger.value && filterValue.value.trim() !== '') {
    data = filterData(props.catalog, filterValue.value, 'title')
  }
  return data
})
const formatFilterTip = computed(() => {
  return formatTransify('UGC_OFFICIAL_WEBSITE_SEARCHED').replace(
    '{num}',
    `<span style="color: #fff">${currentCatalog.value.length.toString()}</span>`
  )
})
const hasObsoleted = computed(() => {
  return !props.openObsoletedBtn || showObsoleted.value
})
const allDocuments = computed(() => {
  return flatDocuments(currentCatalog.value, 'url', hasObsoleted.value)
})
/* --- flat all documents --- */
function flatDocuments(arr: CatalogsConfig[], key: keyof CatalogsConfig, hasObsoleted = true) {
  const newArr: CatalogsConfig[] = []
  arr.forEach((item) => {
    if (item[key] && (hasObsoleted || !item.obsoleted)) {
      newArr.push(item)
    }
    if (item.children) {
      newArr.push(...flatDocuments(item.children, key, hasObsoleted))
    }
  })
  return newArr
}
/* -- Return flat catalogs by filtering -- */
function filterData(arr: CatalogsConfig[], filterVal: string, key: keyof CatalogsConfig = 'title'): CatalogsConfig[] {
  const data = ref<CatalogsConfig[]>([])
  arr.forEach((item) => {
    if (String(item[key]).toLowerCase().includes(filterVal.toLowerCase()) && item.url) {
      const newItem = { ...item, children: [] }
      if (!showObsoleted.value) {
        !newItem.obsoleted && data.value.push(newItem)
      } else {
        data.value.push(newItem)
      }
    }
    if (item.children) {
      const children = filterData(item.children, filterVal, key)
      data.value.push(...children)
    }
  })
  return data.value
}
function openMenu() {
  showMenu.value = true
  document.body.classList.add('overflow-hidden', 'pointer-events-none')
}
function closeMenu() {
  showMenu.value = false
  document.body.classList.remove('overflow-hidden', 'pointer-events-none')
}
function toggleTab(value: string) {
  emit('update:active-tab', value)
}
function setScrollAreaY() {
  const activeElem = document.querySelector('.gfr-catalog-header.is__active') as HTMLElement
  scrollAreaY.value = activeElem ? activeElem.offsetTop : 0
}
watch(
  () => route.params.id,
  async () => {
    closeMenu()
    if (route.params.id) {
      setDocuments(allDocuments.value, 'url', String(route.params.id), filterTrigger.value)
      await nextTick()
      setScrollAreaY()
    }
  }
)
watch(
  () => showObsoleted.value,
  () => {
    storage.setItem('showObsoleted', showObsoleted.value)
    setDocuments(allDocuments.value, 'url', String(route.params.id), filterTrigger.value)
  }
)
watch(
  () => [filterTrigger.value, props.activeTab],
  async () => {
    resetScroll.value = true
    await nextTick()
    setScrollAreaY()
  }
)
onMounted(async () => {
  showObsoleted.value = storage.getItem('showObsoleted') || false
  setDocuments(allDocuments.value, 'url', String(route.params.id), filterTrigger.value)
  await nextTick()
  setScrollAreaY()
})
onBeforeUnmount(() => {
  closeMenu()
})
</script>
<style lang="scss" scoped>
.app-docs-aside {
  @apply relative;
}
.app-docs-aside-inner {
  @apply sticky top-[104px] h-[calc(100vh-104px)] w-[312px] flex-shrink-0 ltr:border-r rtl:border-l border-[#4D5158];
}
.app-docs-aside-inner {
  @apply max-h-full;
}
.app-docs-catalog-wrapper {
  @apply relative w-full h-[calc(100%-20px)] pr-2;
}

.app-docs-tabs {
  @apply grid h-10 w-full cursor-pointer grid-cols-2 grid-rows-1 max-lg:h-16;
}
.app-docs-tab {
  @apply flex items-center justify-center bg-[#363A41] text-xl font-medium leading-9 text-[#C8CCD3] transition-colors duration-200 data-[state=active]:bg-primary data-[state=active]:text-black max-lg:text-[32px];
}
</style>
